import { resolveSharedSpaceLinkPrefix } from "./sharedSpaceUrlPrefix";

const APP_SHARED_SPACE_PREFIX = "https://app.pitchview.io/2/";
const TEMPLATE_LINK_MARKER_REGEX = /\{\{\s*link\s*\}\}|<link>/i;

export type ClipboardPayload = {
  plainText: string;
  htmlText: string;
};

export type ClipboardTypeLabels = {
  video: string;
  image: string;
};

export type DocumentLinkEntry = {
  title: string;
  url: string;
};

export type SharedSpaceSectionEntry = {
  sectionTitle: string;
  documents: Array<DocumentLinkEntry>;
};

const getClipboardMimeType = ({
  originalMimeType,
  mimeType,
}: {
  originalMimeType?: string | null;
  mimeType?: string | null;
}): string | null => originalMimeType || mimeType || null;

export const getClipboardDocumentTypeLabel = ({
  originalMimeType,
  mimeType,
  typeLabels,
}: {
  originalMimeType?: string | null;
  mimeType?: string | null;
  typeLabels: ClipboardTypeLabels;
}): string | null => {
  const clipboardMimeType = getClipboardMimeType({
    originalMimeType,
    mimeType,
  });

  if (!clipboardMimeType) {
    return null;
  }

  switch (clipboardMimeType) {
    case "application/pdf":
      return "PDF";
    case "application/mp4":
    case "video/mp4":
      return typeLabels.video;
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return "PPT";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "DOCX";
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "XLSX";
    default:
      return clipboardMimeType.startsWith("image/") ? typeLabels.image : null;
  }
};

export const buildClipboardDocumentTitle = ({
  displayTitle,
  originalMimeType,
  mimeType,
  typeLabels,
}: {
  displayTitle: string;
  originalMimeType?: string | null;
  mimeType?: string | null;
  typeLabels: ClipboardTypeLabels;
}): string => {
  const typeLabel = getClipboardDocumentTypeLabel({
    originalMimeType,
    mimeType,
    typeLabels,
  });

  return typeLabel ? `${displayTitle} (${typeLabel})` : displayTitle;
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const normalizeLineBreaks = (value: string): string =>
  value.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

const toPlainHtml = (value: string): string =>
  escapeHtml(value).replace(/\n/g, "<br>");

const startsWithLineBreak = (value: string): boolean => /^\s*\n/.test(value);

const endsWithLineBreak = (value: string): boolean => /\n\s*$/.test(value);

const buildStyledSharedSpaceHtml = ({
  mainLink,
  mainLinkText,
  sections,
}: {
  mainLink: string;
  mainLinkText: string;
  sections: Array<SharedSpaceSectionEntry>;
}): string => {
  const sectionHtml = sections
    .filter((section) => section.documents.length > 0)
    .map((section) => {
      const items = section.documents
        .map(
          (doc) =>
            `<li><a href="${escapeHtml(doc.url)}">${escapeHtml(doc.title)}</a></li>`,
        )
        .join("");

      return `${escapeHtml(section.sectionTitle)}<ul>${items}</ul>`;
    })
    .join("");

  return `<p><b><a href="${escapeHtml(mainLink)}">${escapeHtml(mainLinkText)}</a></b></p>${sectionHtml}`;
};

const combineTemplateText = ({
  sourceLineText,
  templateText,
}: {
  sourceLineText?: string | null;
  templateText?: string | null;
}): string => {
  const normalizedSourceLine = normalizeLineBreaks(sourceLineText ?? "").trim();
  const normalizedTemplateText = normalizeLineBreaks(templateText ?? "");

  if (normalizedSourceLine.length === 0) {
    return normalizedTemplateText;
  }

  if (normalizedTemplateText.length === 0) {
    return normalizedSourceLine;
  }

  return `${normalizedSourceLine}\n${normalizedTemplateText}`;
};

const resolvePrefix = (urlPrefix?: string, fallbackPrefix?: string): string => {
  return resolveSharedSpaceLinkPrefix(
    urlPrefix,
    fallbackPrefix || APP_SHARED_SPACE_PREFIX,
  );
};

export const isRochePrefix = (urlPrefix?: string): boolean => {
  if (!urlPrefix) {
    return false;
  }

  try {
    const hostname = new URL(urlPrefix).hostname.toLowerCase();
    return hostname === "roche.de" || hostname.endsWith(".roche.de");
  } catch (err) {
    return false;
  }
};

export const shouldUseAppHostForDocumentLinks = ({
  urlPrefix,
  forcePitchviewPrefix = false,
}: {
  urlPrefix?: string;
  forcePitchviewPrefix?: boolean;
}): boolean => forcePitchviewPrefix || isRochePrefix(urlPrefix);

export const buildSpaceLink = ({
  urlPrefix,
  urlSegment,
  fallbackPrefix,
}: {
  urlPrefix?: string;
  urlSegment: string;
  fallbackPrefix?: string;
}): string => {
  return `${resolvePrefix(urlPrefix, fallbackPrefix)}${urlSegment}`;
};

export const buildDocumentLink = ({
  urlPrefix,
  urlSegment,
  spaceItemId,
  fallbackPrefix,
  useAppHostForDocuments = false,
}: {
  urlPrefix?: string;
  urlSegment: string;
  spaceItemId: string;
  fallbackPrefix?: string;
  useAppHostForDocuments?: boolean;
}): string => {
  const basePrefix = useAppHostForDocuments
    ? resolvePrefix(undefined, fallbackPrefix)
    : resolvePrefix(urlPrefix, fallbackPrefix);
  const baseLink = `${basePrefix}${urlSegment}`;
  const sanitizedBase = baseLink.endsWith("/")
    ? baseLink.slice(0, -1)
    : baseLink;

  return `${sanitizedBase}/${spaceItemId}`;
};

export const buildSharedSpaceClipboardPayload = ({
  mainLink,
  mainLinkText,
  sections,
  templateText,
  sourceLineText,
}: {
  mainLink: string;
  mainLinkText: string;
  sections: Array<SharedSpaceSectionEntry>;
  templateText?: string | null;
  sourceLineText?: string | null;
}): ClipboardPayload => {
  const styledHtml = buildStyledSharedSpaceHtml({
    mainLink,
    mainLinkText,
    sections,
  });
  const normalizedTemplateText = combineTemplateText({
    sourceLineText,
    templateText,
  });

  if (normalizedTemplateText.trim().length === 0) {
    return {
      plainText: mainLink,
      htmlText: styledHtml,
    };
  }

  const marker = normalizedTemplateText.match(TEMPLATE_LINK_MARKER_REGEX);
  if (marker && marker.index !== undefined) {
    const before = normalizedTemplateText.slice(0, marker.index);
    const after = normalizedTemplateText.slice(marker.index + marker[0].length);
    const beforeHtml = toPlainHtml(before);
    const afterHtml = toPlainHtml(after);
    const beforeSeparator =
      before.length > 0 && !endsWithLineBreak(before) ? "<br>" : "";
    const afterSeparator =
      after.length > 0 && !startsWithLineBreak(after) ? "<br>" : "";

    return {
      plainText: mainLink,
      htmlText: `${beforeHtml}${beforeSeparator}${styledHtml}${afterSeparator}${afterHtml}`,
    };
  }

  const separator = endsWithLineBreak(normalizedTemplateText) ? "" : "<br>";

  return {
    plainText: mainLink,
    htmlText: `${toPlainHtml(normalizedTemplateText)}${separator}${styledHtml}`,
  };
};

export const buildDocumentClipboardPayload = ({
  documentLink,
  documentTitle,
}: {
  documentLink: string;
  documentTitle: string;
}): ClipboardPayload => {
  return {
    plainText: documentLink,
    htmlText: `<a href="${escapeHtml(documentLink)}">${escapeHtml(documentTitle)}</a>`,
  };
};

export const copyClipboardPayload = async (
  { plainText, htmlText }: ClipboardPayload,
  signal?: AbortSignal,
): Promise<void> => {
  signal?.throwIfAborted();
  const clipboard = navigator.clipboard;
  if (!clipboard) {
    throw new Error("Failed to copy link to clipboard.");
  }

  if (
    typeof ClipboardItem !== "undefined" &&
    typeof clipboard.write === "function"
  ) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([htmlText], { type: "text/html" }),
          "text/plain": new Blob([plainText], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch (err) {
      // Fallback to plain text if rich copy is not supported.
    }
  }

  signal?.throwIfAborted();
  try {
    await clipboard.writeText(plainText);
  } catch (err) {
    throw new Error("Failed to copy link to clipboard.");
  }
};
